import React, { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    // Navbar chrome
    FaSignOutAlt,
    FaAngleLeft,
    FaAngleRight,
    FaGripLines,
    FaBars,
    FaChevronLeft,
    FaChevronUp,
    FaChevronDown,
    FaExternalLinkAlt,
    // Menu icons - one per entry, see NAV_CONFIG
    FaChartPie,
    FaBusinessTime,
    FaHourglassHalf,
    FaUserClock,
    FaStopwatch,
    FaListAlt,
    FaClipboardCheck,
    FaRegEdit,
    FaCalendarTimes,
    FaTachometerAlt,
    FaCheckDouble,
    FaEnvelopeOpenText,
    FaUmbrellaBeach,
} from "react-icons/fa";
import { apiFetch } from "./utils/api";
import "./left_navbar.css";

// Category this app owns in the shared dadmin.access_levels matrix. Every
// `page` below is looked up as (ACCESS_CATEGORY, page) - matching on page_name
// alone used to be enough only by luck: "User Interface" exists under six
// different categories, so a page-name-only lookup resolves to whichever row
// sorts first.
const ACCESS_CATEGORY = "dTime";

// ── Navigation tree ─────────────────────────────────────────────────────────
// Single source of truth for the whole menu. Everything else in this file is
// derived from it: the set of collapsible section ids, the route→section map
// that drives the accordion, and the rendered markup. Adding a page means
// adding ONE entry here - no other edit anywhere in this file.
//
// Node shapes:
//   section  { id, label, icon, color, children }   collapsible, top level
//   group    { id, label, icon, color, children }   collapsible, nested
//   route    { label, icon, color, to }             internal <NavLink>
//   external { label, icon, color, href }           opens in a new tab
//
// Access gating - both mechanisms this app already used, now declarative and
// wired into the renderer instead of being scattered through the JSX:
//   roles: [...]  visible only to those emp_access_level values
//   page:  "..."  visible only when access_levels has a 1 for this role in
//                 the (ACCESS_CATEGORY, page) row
// A collapsible section is hidden when it has no visible children, so a role
// can never see an empty section header.
//
// Icons: every entry has its OWN icon - no two rows share a glyph, so a row is
// identifiable from its shape alone in the 50px icon-only rail. (The one
// deliberate repeat allowed by the design - FaDesktop on external "User
// Interface" links - has no entry in this app yet.)
//
// Colours - ONE rule, no exceptions: a row's colour is a tint of the hue owned
// by the collapsible group that directly contains it. So the hue tells you
// which module a row belongs to even after its header has scrolled off, while
// the tint keeps the row itself individually identifiable (every value in this
// file is unique). No semantic exceptions - the beach umbrella is blue because
// Holiday is a top-level dTime row, not orange.
//
// Every value here sits inside a fixed HSL band - lightness 58-73%, saturation
// 62-83% - and that band is the point. Tints above ~80% lightness (the
// xxx-100/200 end of a normal colour scale) collapse to white against the dark
// navbar and lose the family cue entirely; below ~55% they sink into the
// background. Adding a row means picking a hue near its group's and staying
// inside the band, which also guarantees the >=3:1 contrast the icon needs once
// the 0.88 resting opacity in left_navbar.css is composited in.
//
// The shared rows below use the exact same hex values as dAdmin's navbar, so
// the two apps read as one suite.
//
// The value is piped into the row as the --icon-color CSS var and also tints
// the active indicator bar.
const NAV_CONFIG = [
    { label: "Dashboard", icon: FaChartPie, color: "#51acd6", to: "/Dashboard" },
    // ── Shift Management · cyan ─────────────────────────────────────────────
    {
        id: "ShiftManagement", label: "Shift Management", icon: FaBusinessTime, color: "#51d6d0",
        roles: ["Admin", "Sub Admin"],
        children: [
            { label: "Shifts", icon: FaHourglassHalf, color: "#60cce2", to: "/Shift" },
            { label: "Assign Shifts", icon: FaUserClock, color: "#6fbceb", to: "/Assign_Shifts" },
        ],
    },
    // ── Timesheet · blue ────────────────────────────────────────────────────
    {
        id: "Timesheet", label: "Timesheet", icon: FaStopwatch, color: "#51bcd6",
        children: [
            { label: "Timesheet Summary", icon: FaListAlt, color: "#5f79e3", to: "/timesheet-summary" },
            {
                label: "Timesheet Approvals", icon: FaClipboardCheck, color: "#608fe2",
                to: "/Timesheet_approvals", roles: ["Admin", "Sub Admin", "Manager"],
            },
            { label: "Timesheet Entry", icon: FaRegEdit, color: "#746feb", to: "/Timesheet_entry" },
        ],
    },
    // ── Leave Management · violet ───────────────────────────────────────────
    {
        id: "LeaveManagement", label: "Leave Management", icon: FaCalendarTimes, color: "#9056d7",
        children: [
            { label: "Leave Dashboard", icon: FaTachometerAlt, color: "#ad60e2", to: "/Leave_dashboard" },
            {
                label: "Leave Approvals", icon: FaCheckDouble, color: "#e681f3",
                to: "/Leave_approvals", page: "Leave Approvals",
            },
            { label: "My Leave Requests", icon: FaEnvelopeOpenText, color: "#ef7be3", to: "/My_leave_requests" },
        ],
    },
    { label: "Holiday", icon: FaUmbrellaBeach, color: "#51a1d6", to: "/Holiday" },
];

// Every collapsible id in the tree, derived. Used to drop stale keys out of the
// persisted open/closed state when a section is renamed or removed.
const SECTION_IDS = (() => {
    const ids = [];
    const walk = (items) => {
        for (const item of items) {
            if (!item.children) continue;
            if (item.id) ids.push(item.id);
            walk(item.children);
        }
    };
    walk(NAV_CONFIG);
    return ids;
})();

// Route → ancestor section ids, derived from NAV_CONFIG. Drives the accordion:
// navigating to a path opens exactly its ancestors and collapses everything
// else. Paths with no ancestors (Dashboard, Holiday) collapse every section.
const PATH_TO_SECTIONS = (() => {
    const map = {};
    const walk = (items, ancestors) => {
        for (const item of items) {
            if (item.children) {
                walk(item.children, item.id ? [...ancestors, item.id] : ancestors);
            } else if (item.to) {
                map[item.to.toLowerCase()] = ancestors;
            }
        }
    };
    walk(NAV_CONFIG, []);
    return map;
})();

// True when `item` is, or contains, the page at `pathname`. Used to accent a
// collapsed section header so the current location is still discoverable in
// icon-only mode (where labels and chevrons are hidden).
function containsPath(item, pathname) {
    if (item.to) return item.to.toLowerCase() === pathname;
    return (item.children || []).some(child => containsPath(child, pathname));
}

// Open/closed state for every collapsible section, persisted as one JSON blob
// so it survives route changes, remounts and reloads.
const OPEN_STATE_KEY = "dtime.nav.open";

function usePersistedOpenMap() {
    const [map, setMap] = useState(() => {
        try {
            const raw = localStorage.getItem(OPEN_STATE_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (!parsed || typeof parsed !== "object") return {};
            // Keep only ids that still exist in NAV_CONFIG.
            return Object.fromEntries(
                Object.entries(parsed).filter(([id]) => SECTION_IDS.includes(id))
            );
        } catch {
            return {};
        }
    });
    const set = useCallback((next) => {
        setMap(prev => {
            const val = typeof next === "function" ? next(prev) : next;
            try { localStorage.setItem(OPEN_STATE_KEY, JSON.stringify(val)); } catch { }
            return val;
        });
    }, []);
    return [map, set];
}

function LeftNavbar({ navSize, setNavSize }) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [openMap, setOpenMap] = usePersistedOpenMap();
    const [loggedInEmp, setLoggedInEmp] = useState(null);
    const [accessLevels, setAccessLevels] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname.toLowerCase();

    useEffect(() => {
        fetchloginedEmployees();
        fetchAccessLevels();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            if (data[0].emp_id) {
                setLoggedInEmp(data[0]);
            }

        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    const fetchAccessLevels = async () => {
        try {
            const res = await apiFetch(`/api/login/get-access`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
            if (data.success) {
                setAccessLevels(data.data);
            }
        } catch (err) {
            console.error("❌ Error fetching access levels:", err);
        }
    };

    // Matrix lookup for one page of THIS app. Matched on category + page_name -
    // page_name alone is not unique across the shared table.
    const hasPageAccess = useCallback((pageName) => {
        if (!loggedInEmp || accessLevels.length === 0) return false;

        const role = loggedInEmp.emp_access_level; // Admin, Sub Admin, Manager, User

        const page = accessLevels.find(
            p => p.category === ACCESS_CATEGORY && p.page_name === pageName
        );
        if (!page) return false;

        if (role === "Admin") return page.admin_access === 1;
        if (role === "Sub Admin") return page.subadmin_access === 1;
        if (role === "Manager") return page.manager_access === 1;
        if (role === "User") return page.user_access === 1;

        return false;
    }, [loggedInEmp, accessLevels]);

    // Prune NAV_CONFIG down to what this role may see. A row survives its own
    // gate (roles / page); a collapsible section additionally needs at least
    // one surviving child, so an empty section header can never render.
    const visibleNav = useMemo(() => {
        const allowed = (item) => {
            if (item.roles && !item.roles.includes(loggedInEmp?.emp_access_level)) return false;
            if (item.page && !hasPageAccess(item.page)) return false;
            return true;
        };
        const prune = (items) => {
            const out = [];
            for (const item of items) {
                if (!allowed(item)) continue;
                if (item.children) {
                    const kids = prune(item.children);
                    if (kids.length) out.push({ ...item, children: kids });
                } else {
                    out.push(item);
                }
            }
            return out;
        };
        return prune(NAV_CONFIG);
    }, [loggedInEmp, hasPageAccess]);

    // Accordion behaviour - when the URL changes, open only the sections that
    // own the new route and collapse every other section. Runs on every
    // pathname change (NavLink click + browser back/forward + direct URL).
    // Manual section toggles still work normally; the next navigation
    // re-collapses them.
    useEffect(() => {
        const ancestors = PATH_TO_SECTIONS[pathname] || [];
        setOpenMap(Object.fromEntries(ancestors.map(id => [id, true])));
    }, [pathname, setOpenMap]);

    const toggleSection = useCallback((id) => {
        setOpenMap(prev => ({ ...prev, [id]: !prev[id] }));
    }, [setOpenMap]);

    const handleNavSizeChange = (size) => {
        setNavSize(size);
        setDropdownOpen(false);
    };

    const handleLogout = async () => {
        await apiFetch(`/api/login/logout`, {
            method: "POST",
            credentials: "include",
        });

        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
    };

    // Recursive renderer. `depth` feeds the --depth CSS var that indents nested
    // rows in full view and is ignored (rows centre instead) in icon-only view.
    // In icon-only view every row also carries a title so the label is still
    // reachable on hover.
    const renderItems = (items, depth) => items.map(item => {
        const Icon = item.icon;
        const key = item.id || item.to || item.href || item.label;
        const tip = navSize === "icon-only" ? item.label : undefined;
        // --icon-color tints both the glyph and the active indicator bar;
        // --depth drives the nested indent. Both are read by left_navbar.css.
        const rowStyle = { "--depth": String(depth), "--icon-color": item.color };

        if (item.children) {
            const open = !!openMap[item.id];
            const holdsActive = containsPath(item, pathname);
            const onToggle = () => toggleSection(item.id);
            return (
                <li key={key} className="nav-group">
                    <div
                        className={`nav-row nav-group-header${open ? " open" : ""}${holdsActive ? " holds-active" : ""}`}
                        style={rowStyle}
                        title={tip}
                        role="button"
                        tabIndex={0}
                        aria-expanded={open}
                        onClick={onToggle}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); }
                        }}
                    >
                        <Icon className="nav-icon" />
                        {navSize === "full" && <span className="nav-label">{item.label}</span>}
                        {/* Chevron only in full view - in the 50px rail it would
                            sit on top of the icon, at every depth. */}
                        {navSize === "full" && (
                            <span className="arrow-icon">
                                {open ? <FaChevronUp /> : <FaChevronDown />}
                            </span>
                        )}
                    </div>
                    {open && <ul className="submenu">{renderItems(item.children, depth + 1)}</ul>}
                </li>
            );
        }

        if (item.href) {
            return (
                <li key={key}>
                    {/* class is "nav-external", not "nav-link" - Bootstrap owns
                        .nav-link and would override display/colour on the row */}
                    <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="nav-row nav-external"
                        style={rowStyle}
                        title={tip}
                    >
                        <Icon className="nav-icon" />
                        {navSize === "full" && <span className="nav-label">{item.label}</span>}
                        {navSize === "full" && <FaExternalLinkAlt className="nav-ext-icon" />}
                    </a>
                </li>
            );
        }

        return (
            <li key={key}>
                <NavLink
                    to={item.to}
                    className={({ isActive }) => `nav-row${isActive ? " active" : ""}`}
                    style={rowStyle}
                    title={tip}
                >
                    <Icon className="nav-icon" />
                    {navSize === "full" && <span className="nav-label">{item.label}</span>}
                </NavLink>
            </li>
        );
    });

    return (
        <div className={`left-navbar ${navSize}`}>
            {navSize !== "hidden" && (
                <ul className="navbar-menu">
                    {renderItems(visibleNav, 0)}
                </ul>
            )}

            <div className="logout-container">
                {/* View-size switcher */}
                <div
                    className="view-dropdown-arrow"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={dropdownOpen}
                    aria-label="Change navigation size"
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDropdownOpen(o => !o); }
                    }}
                >
                    {navSize === "full" ? <FaAngleLeft className="view-change-icon" /> : <FaAngleRight className="view-change-icon" />}

                    {dropdownOpen && (
                        <div className="view-dropdown-menu">
                            <div className="view-dropdown-item" onClick={() => handleNavSizeChange("full")}>
                                <FaGripLines /> Full View
                            </div>
                            <div className="view-dropdown-item" onClick={() => handleNavSizeChange("icon-only")}>
                                <FaBars /> Icon Only
                            </div>
                            <div className="view-dropdown-item" onClick={() => handleNavSizeChange("hidden")}>
                                <FaChevronLeft /> Hidden
                            </div>
                        </div>
                    )}
                </div>

                {navSize !== "hidden" && (
                    <button
                        type="button"
                        className="nav-row logout-link"
                        style={{ "--icon-color": "#e56c80" }}
                        title={navSize === "icon-only" ? "Logout" : undefined}
                        onClick={handleLogout}
                    >
                        <FaSignOutAlt className="nav-icon" />
                        {navSize === "full" && <span className="nav-label">Logout</span>}
                    </button>
                )}
            </div>
        </div>
    );
}

export default LeftNavbar;
